import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Alert, CircularProgress,
  Divider
} from '@mui/material';
import {
  Send as SendIcon
} from '@mui/icons-material';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';
import { useSelector } from 'react-redux';

const Transfer = () => {
  const fundingBal = useSelector((state) => state.wallet.fundingBalance); // Overall Assets

  const [recipientEmail, setRecipientEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const transferMutation = useMutation(
    async (transferData) => {
      const response = await apiClient.post('/api/transfers', transferData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSuccess('Transfer is processing. You will be notified when the transfer is completed.');
        setRecipientEmail('');
        setAmount('');
        setDescription('');
        setError('');
      },
      onError: (error) => {
        setError(error.response?.data?.message || 'Failed to submit transfer request');
        setSuccess('');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!recipientEmail || !amount) {
      setError('Please fill in all required fields');
      return;
    }

    // Check against Overall Assets balance
    if (parseFloat(amount) > parseFloat(fundingBal)) {
      setError('Insufficient balance in Overall Assets');
      return;
    }

    if (parseFloat(amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    transferMutation.mutate({
      recipient_email: recipientEmail,
      amount: parseFloat(amount),
      destination: 'Overall Asset', // Always Overall Asset
      description: description || 'Fund transfer'
    });
  };

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '800px', mx: 'auto' }}>
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        borderRadius: '12px'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <SendIcon sx={{ fontSize: 32, color: '#F7931A' }} />
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
              Transfer Funds
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 3 }}>
            Transfer funds from your Overall Assets to another user. All transfers require admin approval before processing.
          </Typography>

          {/* Overall Assets Balance Display */}
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            background: 'rgba(247, 147, 26, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(247, 147, 26, 0.3)'
          }}>
            <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 1 }}>
              Available Balance (Overall Assets)
            </Typography>
            <Typography variant="h5" sx={{ color: '#F7931A', fontWeight: 700 }}>
              {formatPrice(fundingBal)}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Recipient Email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                inputProps={{ min: 0, step: 0.01 }}
                helperText={`Available in Overall Assets: ${formatPrice(fundingBal)}`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' },
                  '& .MuiFormHelperText-root': { color: '#6C7293' }
                }}
              />

              <TextField
                fullWidth
                label="Description (Optional)"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                    '&.Mui-focused fieldset': { borderColor: '#F7931A' }
                  },
                  '& .MuiInputLabel-root': { color: '#B7BDC6' }
                }}
              />

              <Divider sx={{ borderColor: '#2B3139' }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    setRecipientEmail('');
                    setAmount('');
                    setDescription('');
                    setError('');
                    setSuccess('');
                  }}
                  sx={{
                    borderColor: '#2B3139',
                    color: '#B7BDC6',
                    '&:hover': { borderColor: '#6C7293' }
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={transferMutation.isLoading}
                  startIcon={transferMutation.isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8841A 0%, #E6C200 100%)'
                    }
                  }}
                >
                  {transferMutation.isLoading ? 'Submitting...' : 'Submit Transfer Request'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Transfer;

